import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { app, auth, firebaseErrorCode, storage, waitForUser } from '../lib/firebase'

/** Samme grænse som Firebase Storage-reglerne: 40 MB pr. billede. */
export const MAX_IMAGE_MB = 40
export const MAX_IMAGE_BYTES = MAX_IMAGE_MB * 1024 * 1024

export interface UploadedImage {
  path: string
  /** Tom for offentlige uploads — reglerne tillader kun admin at læse filerne. */
  url: string
  name: string
}

export function validateImage(file: File): string | null {
  if (!file.type.startsWith('image/')) {
    return `Billedet “${file.name}” kunne ikke bruges. Vælg et andet billede eller fjern det.`
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return 'Billedet er for stort. Maksimal filstørrelse er 40 MB pr. billede.'
  }
  return null
}

function uniqueName(name: string): string {
  const safe = name.replace(/[^a-zA-Z0-9._-]/g, '_')
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  return `${id}-${safe}`
}

interface UploadOptions {
  /**
   * Hent download-URL efter upload. Kræver læseadgang i Storage-reglerne og
   * må derfor KUN bruges af en indlogget administrator. Offentlige
   * henvendelser gemmer udelukkende stien.
   */
  resolveUrls?: boolean
  /**
   * Vent på at Firebase Auth er færdig med at gendanne sessionen, og kræv en
   * indlogget bruger, før der uploades. Bruges til sagsbilleder; offentlige
   * henvendelser uploader uden login.
   */
  requireAuth?: boolean
}

/**
 * Uploader billeder til en fast mappe, fx enquiries/{enquiryId}/ eller cases/{caseId}/.
 * Stien matcher Storage-reglerne præcist: enquiries/{enquiryId}/{fileName}.
 */
export async function uploadImages(
  files: File[],
  folder: string,
  options: UploadOptions = {},
): Promise<UploadedImage[]> {
  let uid: string | null = null

  if (options.requireAuth) {
    // Ingen upload før onAuthStateChanged har bekræftet login-status.
    const user = await waitForUser()
    uid = user?.uid ?? null
    if (!user) {
      const error = new Error('Ingen aktiv Firebase-session') as Error & { code: string }
      error.code = 'auth/no-session'
      throw error
    }
  }

  const uploads = files.map(async (file) => {
    const path = `${folder}/${uniqueName(file.name)}`
    const storageRef = ref(storage, path)

    try {
      await uploadBytes(storageRef, file, { contentType: file.type })
    } catch (error) {
      // Fuld kontekst i konsollen, så en afvist upload kan diagnosticeres præcist.
      // eslint-disable-next-line no-console
      console.error('[Storage] uploadBytes fejlede', {
        code: firebaseErrorCode(error),
        message: error instanceof Error ? error.message : String(error),
        path,
        uid: uid ?? auth.currentUser?.uid ?? null,
        email: auth.currentUser?.email ?? null,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        projectId: app.options.projectId,
        storageBucket: app.options.storageBucket,
        error,
      })

      if (firebaseErrorCode(error) === 'storage/unauthorized') {
        // Præcis sti + UID, så et misforhold til Storage Rules kan ses med det samme.
        // eslint-disable-next-line no-console
        console.error(
          `[Storage] storage/unauthorized — sti: "${path}" · uid: ${
            uid ?? auth.currentUser?.uid ?? 'ingen'
          }`,
        )
      }
      throw error
    }

    let url = ''
    if (options.resolveUrls) {
      try {
        url = await getDownloadURL(storageRef)
      } catch (error) {
        // Manglende læseadgang må aldrig vælte en ellers gennemført upload.
        // eslint-disable-next-line no-console
        console.warn('[Storage] getDownloadURL sprunget over', {
          code: firebaseErrorCode(error),
          path,
        })
      }
    }

    return { path, url, name: file.name }
  })

  return Promise.all(uploads)
}

/** Henter en visnings-URL til en gemt sti. Kræver læseadgang (admin). */
export async function imageUrl(path: string): Promise<string> {
  return getDownloadURL(ref(storage, path))
}

export async function deleteImage(path: string): Promise<void> {
  if (!path) return
  await deleteObject(ref(storage, path))
}
